// 批次上傳音樂（編輯頁歌單的「⋯」→ 批次上傳音樂）：一次拖進很多首第一版音樂，依檔名配到歌，總表看過再上傳。
//   配對規則 lib/musicBatchMatch.ts（先代號、再歌名，不猜）；上傳 lib/musicBatchUpload.ts（同時 3 首，沿用單首上傳流程）。
//   已經有音樂的歌一律跳過：換音樂會刪舊檔，舊檔之後要拿來比對，批次絕不蓋掉它。
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Ban, Check, LoaderCircle, RotateCw, Upload, X } from 'lucide-react';
import Icon from './Icon';
import { confirmDialog } from './dialogs';
import './NewProjectDialog.css';
import './MusicBatchDialog.css';
import type { Song } from '../lib/api';
import type { SongMusic } from '../lib/musicApi';
import { matchFileName, planRows, type MatchSong, type RowPlan } from '../lib/musicBatchMatch';
import { BATCH_CONCURRENCY, runBatch, summarizeStates, type ItemState } from '../lib/musicBatchUpload';
import { MUSIC_ACCEPT, MUSIC_FORMATS_TEXT, MUSIC_MAX_BYTES, formatMusicSize, musicFileProblem } from '../lib/songMusic';

interface Props {
  projectId: string;
  /** 最新的歌單（有沒有音樂以它為準） */
  songs: Song[];
  /** 這一首傳好了 */
  onUploaded: (songId: string, music: SongMusic) => void;
  /** 關掉（上傳過東西的話，呼叫的人重新讀歌單） */
  onClose: () => void;
}

interface Row {
  id: number;
  file: File;
  songId: string | null;
  ambiguous: boolean;
  ignored: boolean;
  fileProblem: string | null;
  state: ItemState | null;
}

const SKIP = '__skip';
const MAX_FILES = 200;
let nextId = 1;

const songLabel = (s: Song) => (s.code ? `${s.code}　${s.name}` : s.name);
const hasMusicOf = (s: Song) => !!s.hasMusic || !!s.music;

const PLAN_TEXT: Record<RowPlan, string> = {
  ready: '待上傳',
  ignored: '不上傳',
  hasMusic: '已有音樂（跳過）',
  unmatched: '配不到，請選歌',
  ambiguous: '對到好幾首，請選歌',
  conflict: '有檔案要傳給同一首',
  badFile: '',
};

export default function MusicBatchDialog({ projectId, songs, onUploaded, onClose }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [running, setRunning] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const aliveRef = useRef(true);
  const songsRef = useRef(songs);
  songsRef.current = songs;
  const uploadedRef = useRef(onUploaded);
  uploadedRef.current = onUploaded;

  useEffect(() => {
    aliveRef.current = true;
    panelRef.current?.focus();
    return () => { aliveRef.current = false; };
  }, []);

  const matchSongs: MatchSong[] = useMemo(
    () => songs.map(s => ({ id: s.id, name: s.name, code: s.code ?? null, hasMusic: hasMusicOf(s) })),
    [songs],
  );
  const plans = useMemo(() => planRows(rows, matchSongs), [rows, matchSongs]);

  const addFiles = useCallback((list: FileList | File[]) => {
    const files = Array.from(list);
    if (files.length === 0) return;
    setRows(prev => {
      const seen = new Set(prev.map(r => `${r.file.name}|${r.file.size}|${r.file.lastModified}`));
      const next = [...prev];
      for (const f of files) {
        const k = `${f.name}|${f.size}|${f.lastModified}`;
        if (seen.has(k) || next.length >= MAX_FILES) continue;
        seen.add(k);
        const problem = musicFileProblem(f, MUSIC_MAX_BYTES);
        const m = problem ? { songId: null, candidates: [] as string[] } : matchFileName(f.name, matchSongs);
        next.push({ id: nextId++, file: f, songId: m.songId, ambiguous: m.candidates.length > 1, ignored: false, fileProblem: problem, state: null });
      }
      return next;
    });
  }, [matchSongs]);

  const patch = (id: number, p: Partial<Row>) => setRows(rs => rs.map(r => (r.id === id ? { ...r, ...p } : r)));
  const setState = useCallback((id: number, s: ItemState) => {
    if (!aliveRef.current) return;
    setRows(rs => rs.map(r => (r.id === id ? { ...r, state: s } : r)));
  }, []);

  /** 這一列現在能不能上傳：沒傳過、或上次失敗 / 取消，而且配好的歌還是沒有音樂 */
  const canRun = (r: Row, p: RowPlan) => p === 'ready' && (r.state == null || r.state.kind === 'failed' || r.state.kind === 'cancelled');

  async function start(only?: number) {
    if (running) return;
    // 以最新的歌單再算一次：這段時間別人傳了音樂的歌，不傳
    const latest: MatchSong[] = songsRef.current.map(s => ({ id: s.id, name: s.name, code: s.code ?? null, hasMusic: hasMusicOf(s) }));
    const latestPlans = planRows(rows, latest);
    const todo = rows.filter((r, i) => canRun(r, latestPlans[i]) && (only == null || r.id === only));
    if (todo.length === 0) return;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setRunning(true);
    const byKey = new Map(todo.map(r => [String(r.id), r]));
    await runBatch({
      projectId,
      items: todo.map(r => ({ key: String(r.id), songId: r.songId as string, file: r.file })),
      limit: BATCH_CONCURRENCY,
      signal: ctrl.signal,
      onState: (key, s) => {
        const row = byKey.get(key);
        if (!row) return;
        setState(row.id, s);
        if (s.kind === 'done') uploadedRef.current(row.songId as string, s.music);
      },
    });
    abortRef.current = null;
    if (aliveRef.current) setRunning(false);
  }

  async function requestClose() {
    if (running) {
      const ok = await confirmDialog({
        title: '音樂還在上傳，要關掉嗎？',
        message: '關掉會停下還沒傳完的。已經傳好的會留著。',
        confirmLabel: '停止並關掉',
        destructive: true,
      });
      if (!ok) return;
      abortRef.current?.abort();
    }
    onClose();
  }

  // 傳到一半關頁面 / 重新整理 → 先問一聲
  useEffect(() => {
    if (!running) return;
    const onUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [running]);

  const counts = useMemo(() => summarizeStates(rows.map(r => r.state).filter((s): s is ItemState => !!s)), [rows]);
  const readyCount = rows.filter((r, i) => canRun(r, plans[i])).length;
  const retryCount = rows.filter((r, i) => r.state?.kind === 'failed' && canRun(r, plans[i])).length;
  const finished = !running && (counts.done > 0 || counts.failed > 0);
  const uploadLabel = retryCount > 0 && retryCount === readyCount ? `重試失敗的 ${retryCount} 首` : `上傳 ${readyCount} 首`;

  const statusOf = (r: Row, p: RowPlan): { text: string; tone: 'ok' | 'warn' | 'bad' | 'dim'; progress?: number; spin?: boolean } => {
    const st = r.state;
    if (st) {
      switch (st.kind) {
        case 'queued': return { text: '等待中', tone: 'dim' };
        case 'checking': return { text: '檢查中', tone: 'dim', spin: true };
        case 'uploading': return { text: `上傳中 ${Math.round(st.progress * 100)}%`, tone: 'dim', progress: st.progress };
        case 'done': return { text: '已上傳', tone: 'ok' };
        case 'skipped': return { text: '已有音樂（跳過）', tone: 'warn' };
        case 'cancelled': return { text: '已取消', tone: 'warn' };
        case 'failed': return { text: st.text, tone: 'bad' };
      }
    }
    if (p === 'badFile') return { text: r.fileProblem || '這個檔案不能傳', tone: 'bad' };
    if (p === 'ready') return { text: PLAN_TEXT.ready, tone: 'ok' };
    if (p === 'hasMusic') return { text: PLAN_TEXT.hasMusic, tone: 'warn' };
    if (p === 'ignored') return { text: PLAN_TEXT.ignored, tone: 'dim' };
    return { text: PLAN_TEXT[p], tone: 'bad' };
  };

  return (
    <div className="dialog-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) void requestClose(); }}>
      <div
        ref={panelRef}
        tabIndex={-1}
        className={'dialog mbd' + (dragOver ? ' is-dragover' : '')}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mbd-title"
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); void requestClose(); } }}
        onDragOver={(e) => {
          if (running || !e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false); }}
        onDrop={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragOver(false);
          if (!running) addFiles(e.dataTransfer.files);
        }}
      >
        <header className="dialog__header">
          <h2 id="mbd-title">批次上傳音樂</h2>
          <button type="button" className="dialog__close" onClick={() => void requestClose()} title="關閉" aria-label="關閉"><Icon icon={X} size="md" /></button>
        </header>

        <div className="dialog__body mbd__body">
          <button type="button" className={'mbd__drop' + (rows.length ? ' is-compact' : '')} onClick={() => inputRef.current?.click()} disabled={running}>
            <Icon icon={Upload} size={rows.length ? 'sm' : 'xl'} />
            <strong>{rows.length ? '再加檔案' : '把音樂檔拖到這裡，或點一下選檔'}</strong>
            {!rows.length && <span>可以一次選很多個；{MUSIC_FORMATS_TEXT}，每個最大 {formatMusicSize(MUSIC_MAX_BYTES)}。檔名有歌曲代號（例：S02 搶先看.mp3）最準</span>}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={MUSIC_ACCEPT}
            multiple
            hidden
            onChange={(e) => {
              const fs = e.currentTarget.files;
              if (fs) addFiles(fs);
              e.currentTarget.value = '';
            }}
          />

          {rows.length > 0 && (
            <ul className="mbd__list" aria-label="要上傳的音樂">
              {rows.map((r, i) => {
                const p = plans[i];
                const st = statusOf(r, p);
                const locked = running || r.state?.kind === 'done' || r.state?.kind === 'skipped' || r.state?.kind === 'queued' || r.state?.kind === 'checking' || r.state?.kind === 'uploading';
                const selVal = r.ignored ? SKIP : (r.songId ?? '');
                return (
                  <li key={r.id} className={'mbd__row is-' + st.tone}>
                    <span className="mbd__file" title={r.file.name}>{r.file.name}</span>
                    <select
                      className="mbd__pick"
                      value={selVal}
                      disabled={locked || p === 'badFile'}
                      aria-label={`「${r.file.name}」要放進哪一首歌`}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === SKIP) patch(r.id, { ignored: true, state: null });
                        else patch(r.id, { ignored: false, songId: v || null, ambiguous: false, state: null });
                      }}
                    >
                      {!r.songId && !r.ignored && <option value="">選歌…</option>}
                      <option value={SKIP}>不上傳</option>
                      {songs.map(s => (
                        <option key={s.id} value={s.id} disabled={hasMusicOf(s) && s.id !== r.songId}>
                          {songLabel(s)}{hasMusicOf(s) && !(r.state?.kind === 'done' && s.id === r.songId) ? '（已有音樂）' : ''}
                        </option>
                      ))}
                    </select>
                    <span className="mbd__status" title={st.text}>
                      {st.spin ? <Icon icon={LoaderCircle} size="xs" spin />
                        : st.tone === 'ok' && r.state?.kind === 'done' ? <Icon icon={Check} size="xs" />
                        : st.tone === 'bad' ? <Icon icon={Ban} size="xs" /> : null}
                      {st.progress != null && (
                        <span className="mbd__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(st.progress * 100)} aria-label="上傳進度">
                          <span style={{ width: `${Math.round(st.progress * 100)}%` }} />
                        </span>
                      )}
                      <span className="mbd__status-text">{st.text}</span>
                    </span>
                    {r.state?.kind === 'failed' && !running ? (
                      <button type="button" className="mbd__mini" onClick={() => void start(r.id)} title="只重試這一首" aria-label={`重試「${r.file.name}」`}><Icon icon={RotateCw} size="xs" /></button>
                    ) : !locked ? (
                      <button type="button" className="mbd__mini" onClick={() => setRows(rs => rs.filter(x => x.id !== r.id))} title="從清單拿掉" aria-label={`把「${r.file.name}」從清單拿掉`}><Icon icon={X} size="xs" /></button>
                    ) : <span className="mbd__mini-gap" />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="dialog__footer mbd__footer">
          <span className="mbd__summary" role="status" aria-live="polite">
            {finished
              ? `成功 ${counts.done} 首、失敗 ${counts.failed} 首${counts.skipped ? `、跳過 ${counts.skipped} 首` : ''}`
              : rows.length ? `${rows.length} 個檔案，${readyCount} 首可以上傳` : ''}
          </span>
          <button type="button" className="btn btn--ghost" onClick={() => void requestClose()}>{finished && !running ? '完成' : '取消'}</button>
          <button type="button" className="btn btn--primary" onClick={() => void start()} disabled={running || readyCount === 0}>
            {running ? '上傳中…' : uploadLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
